import { useEffect } from 'react';
import './FlamethrowerAnimation.css';

/**
 * PROFESSIONAL FLAMETHROWER ANIMATION
 * Fire-type special move with continuous flame stream effects
 * 
 * Key Features:
 * - Fire-type special attack (90 base power, 100% accuracy)
 * - 10% chance to cause burn status
 * - Continuous flame stream with realistic fire physics
 * - Professional gaming company quality with fiery timing
 * - Special move animation with beam-like mechanics
 */
export default function FlamethrowerAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate flame trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);
    const angle = Math.atan2(dy, dx) * 180 / Math.PI;

    // Elements to clean up
    const elements = [];

    // PHASE 1: Fire Charge Buildup (0-500ms)
    console.log('Flamethrower Phase 1: Fire Charge Buildup');
    
    // Player enhancement with fire aura
    playerEl.classList.add('flamethrower-user-charge');
    
    // Fire charging aura around player
    const fireAura = document.createElement('div');
    fireAura.className = 'flamethrower-fire-aura';
    fireAura.style.left = `${playerX - 90}px`;
    fireAura.style.top = `${playerY - 90}px`;
    container.appendChild(fireAura);
    elements.push(fireAura);

    // Fire energy particles around player
    for (let i = 0; i < 16; i++) {
      const energyParticle = document.createElement('div');
      energyParticle.className = 'flamethrower-energy-particle';
      const particleAngle = (i / 16) * 360;
      const radius = 60 + (i % 4) * 12;
      const particleX = playerX + Math.cos(particleAngle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(particleAngle * Math.PI / 180) * radius;
      energyParticle.style.left = `${particleX - 4}px`;
      energyParticle.style.top = `${particleY - 4}px`;
      energyParticle.style.animationDelay = `${i * 25}ms`;
      energyParticle.textContent = '🔥';
      container.appendChild(energyParticle);
      elements.push(energyParticle);
    }

    // Heat distortion effects
    for (let i = 0; i < 10; i++) {
      const heatWave = document.createElement('div');
      heatWave.className = 'flamethrower-heat-wave';
      const heatX = playerX + (Math.random() - 0.5) * 120;
      const heatY = playerY + (Math.random() - 0.5) * 80;
      heatWave.style.left = `${heatX - 8}px`;
      heatWave.style.top = `${heatY - 8}px`;
      heatWave.style.animationDelay = `${i * 40}ms`;
      container.appendChild(heatWave);
      elements.push(heatWave);
    }

    // PHASE 2: Continuous Flame Stream (500-1500ms)
    const phase2Timer = setTimeout(() => {
      console.log('Flamethrower Phase 2: Continuous Flame Stream');
      
      // Create multiple flame bursts instead of stream
      for (let i = 0; i < 12; i++) {
        const burstTimer = setTimeout(() => {
          const flameBurst = document.createElement('div');
          flameBurst.className = 'flamethrower-flame-burst';
          const progress = (i / 12) + (Math.random() - 0.5) * 0.1;
          const burstX = playerX + (dx * progress) + (Math.random() - 0.5) * 25;
          const burstY = playerY + (dy * progress) + (Math.random() - 0.5) * 25;
          flameBurst.style.left = `${burstX - 8}px`;
          flameBurst.style.top = `${burstY - 8}px`;
          flameBurst.textContent = '🔥';
          container.appendChild(flameBurst);
          elements.push(flameBurst);
        }, i * 60);
        elements.push({ timer: burstTimer, type: 'timer' });
      }
      
      // Flame particles along the stream
      for (let i = 0; i < 20; i++) {
        const flameTimer = setTimeout(() => {
          const flameParticle = document.createElement('div');
          flameParticle.className = 'flamethrower-flame-particle';
          const progress = (i / 20) + (Math.random() - 0.5) * 0.1;
          const particleX = playerX + (dx * progress) + (Math.random() - 0.5) * 30;
          const particleY = playerY + (dy * progress) + (Math.random() - 0.5) * 30;
          flameParticle.style.left = `${particleX - 6}px`;
          flameParticle.style.top = `${particleY - 6}px`;
          flameParticle.textContent = '🔥';
          container.appendChild(flameParticle);
          elements.push(flameParticle);
        }, i * 40);
        elements.push({ timer: flameTimer, type: 'timer' });
      }
      
      // Heat shimmer effects along stream
      for (let i = 0; i < 12; i++) {
        const shimmerTimer = setTimeout(() => {
          const shimmer = document.createElement('div');
          shimmer.className = 'flamethrower-heat-shimmer';
          const progress = i / 12;
          const shimmerX = playerX + (dx * progress);
          const shimmerY = playerY + (dy * progress);
          shimmer.style.left = `${shimmerX - 10}px`;
          shimmer.style.top = `${shimmerY - 10}px`;
          container.appendChild(shimmer);
          elements.push(shimmer);
        }, i * 60);
        elements.push({ timer: shimmerTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Fire Impact and Burn (1500-2000ms)
    const phase3Timer = setTimeout(() => {
      console.log('Flamethrower Phase 3: Fire Impact and Burn');
      
      // Opponent hit reaction
      opponentEl.classList.add('flamethrower-opponent-hit');
      
      // Massive fire impact explosion
      const fireImpact = document.createElement('div');
      fireImpact.className = 'flamethrower-fire-impact';
      fireImpact.style.left = `${opponentX - 120}px`;
      fireImpact.style.top = `${opponentY - 120}px`;
      container.appendChild(fireImpact);
      elements.push(fireImpact);
      
      // Fire burst particles
      for (let i = 0; i < 25; i++) {
        const burstParticle = document.createElement('div');
        burstParticle.className = 'flamethrower-burst-particle';
        const burstAngle = (i / 25) * 360;
        const burstRadius = 70 + Math.random() * 50;
        const burstX = opponentX + Math.cos(burstAngle * Math.PI / 180) * burstRadius;
        const burstY = opponentY + Math.sin(burstAngle * Math.PI / 180) * burstRadius;
        burstParticle.style.left = `${burstX - 5}px`;
        burstParticle.style.top = `${burstY - 5}px`;
        burstParticle.style.animationDelay = `${i * 12}ms`;
        burstParticle.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * 35}px`);
        burstParticle.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * 35}px`);
        burstParticle.textContent = '🔥';
        container.appendChild(burstParticle);
        elements.push(burstParticle);
      }
      
      // Fire shockwaves
      for (let i = 0; i < 3; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'flamethrower-shockwave';
          shockwave.style.left = `${opponentX - 80}px`;
          shockwave.style.top = `${opponentY - 80}px`;
          shockwave.style.animationDelay = `${i * 100}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 120);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }
      
      // Burn status effect (10% chance)
      const burnEffect = document.createElement('div');
      burnEffect.className = 'flamethrower-burn-effect';
      burnEffect.style.left = `${opponentX - 60}px`;
      burnEffect.style.top = `${opponentY - 60}px`;
      container.appendChild(burnEffect);
      elements.push(burnEffect);
      
      // Burn indicator
      const burnIndicator = document.createElement('div');
      burnIndicator.className = 'flamethrower-burn-indicator';
      burnIndicator.style.left = `${opponentX - 25}px`;
      burnIndicator.style.top = `${opponentY - 90}px`;
      burnIndicator.textContent = 'BURN';
      container.appendChild(burnIndicator);
      elements.push(burnIndicator);
    }, 1500);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // CLEANUP (2000ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Flamethrower: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('flamethrower-user-charge');
      opponentEl.classList.remove('flamethrower-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 2000);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('flamethrower-user-charge');
      opponentEl.classList.remove('flamethrower-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

FlamethrowerAnimation.moveType = "fire";
